import type { ViewportPoint } from "./semantic-types.ts";

export type MapViewportSize = { readonly width: number; readonly height: number };
export type MapRect = { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number };
export type SafeMapAperture = MapRect & {
  readonly width: number;
  readonly height: number;
  readonly center: ViewportPoint;
};
export type MapOcclusion = { readonly bottomEdge?: number };

const COMPACT_PORTRAIT_TOP = 180;
const COMPACT_PORTRAIT_BOTTOM = 180;
const COMPACT_LANDSCAPE_TOP = 88;
const COMPACT_LANDSCAPE_BOTTOM = 92;

/**
 * The part of the full-screen atlas that is not obscured by persistent chrome.
 * Keep this as the single geometry contract for framing, labels and coverage.
 */
export function safeMapAperture(viewport: MapViewportSize, compact: boolean, padding = 0, occlusion: MapOcclusion = {}): SafeMapAperture {
  const width = finiteDimension(viewport.width);
  const height = finiteDimension(viewport.height);
  const inset = Math.max(0, Number.isFinite(padding) ? padding : 0);
  const landscape = width > height;
  const reservedTop = compact ? (landscape ? COMPACT_LANDSCAPE_TOP : COMPACT_PORTRAIT_TOP) : 0;
  const reservedBottom = compact ? (landscape ? COMPACT_LANDSCAPE_BOTTOM : COMPACT_PORTRAIT_BOTTOM) : 0;
  const left = Math.min(width / 2, inset);
  const right = Math.max(left, width - inset);
  const top = Math.min(height / 2, reservedTop + inset);
  const chromeBottom = height - reservedBottom;
  const occluderBottom = occlusion.bottomEdge === undefined
    ? height
    : Math.max(0, Math.min(height, finiteDimension(occlusion.bottomEdge)));
  const bottom = Math.max(top, Math.min(chromeBottom, occluderBottom) - inset);
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
    center: { x: (left + right) / 2, y: (top + bottom) / 2 },
  };
}

/** Measures the currently open mobile detail sheet against the map element. */
export function safeMapApertureForElement(
  viewport: MapViewportSize,
  compact: boolean,
  mapElement: HTMLElement,
  padding = 0,
): SafeMapAperture {
  const responsiveChrome = compactChromeActive(viewport, compact);
  const aperture = safeMapAperture(viewport, responsiveChrome, padding);
  const root = mapElement.closest<HTMLElement>("#app");
  const panel = root?.querySelector<HTMLElement>(".panel.open") ?? null;
  if (panel === null) return aperture;
  const mapRect = mapElement.getBoundingClientRect();
  const panelRect = panel.getBoundingClientRect();
  const horizontalOverlap = Math.min(mapRect.right, panelRect.right) - Math.max(mapRect.left, panelRect.left);
  if (horizontalOverlap <= 0 || panelRect.top >= mapRect.bottom || panelRect.bottom <= mapRect.top) {
    return aperture;
  }
  return subtractMapOccluder(aperture, {
    left: panelRect.left - mapRect.left,
    top: panelRect.top - mapRect.top,
    right: panelRect.right - mapRect.left,
    bottom: panelRect.bottom - mapRect.top,
  }, padding);
}

export function compactChromeActive(viewport: MapViewportSize, compact: boolean): boolean {
  return finiteDimension(viewport.width) <= 52 * 16
    || (compact && finiteDimension(viewport.height) <= 32 * 16);
}

/** Chooses the largest non-overlapping rectangular map surface around an occluder. */
export function subtractMapOccluder(aperture: SafeMapAperture, occluder: MapRect, gap = 0): SafeMapAperture {
  const overlap = intersect(aperture, occluder);
  if (overlap === null) return aperture;
  const inset = Math.max(0, Number.isFinite(gap) ? gap : 0);
  const candidates: MapRect[] = [
    { ...aperture, bottom: Math.max(aperture.top, overlap.top - inset) },
    { ...aperture, right: Math.max(aperture.left, overlap.left - inset) },
    { ...aperture, top: Math.min(aperture.bottom, overlap.bottom + inset) },
    { ...aperture, left: Math.min(aperture.right, overlap.right + inset) },
  ];
  const selected = candidates.sort((left, right) => rectArea(right) - rectArea(left))[0] ?? aperture;
  return apertureFromRect(selected);
}

export function pointInsideSafeMapAperture(point: ViewportPoint, aperture: SafeMapAperture, radius = 0): boolean {
  const extent = Math.max(0, Number.isFinite(radius) ? radius : 0);
  return point.x + extent >= aperture.left
    && point.x - extent <= aperture.right
    && point.y + extent >= aperture.top
    && point.y - extent <= aperture.bottom;
}

/** Returns the smallest screen-space translation that places a rect in the aperture. */
export function translateRectIntoSafeMapAperture(rect: MapRect, aperture: SafeMapAperture): ViewportPoint {
  const width = Math.max(0, rect.right - rect.left);
  const height = Math.max(0, rect.bottom - rect.top);
  return {
    x: axisTranslation(rect.left, rect.right, width, aperture.left, aperture.right, aperture.center.x),
    y: axisTranslation(rect.top, rect.bottom, height, aperture.top, aperture.bottom, aperture.center.y),
  };
}

function axisTranslation(start: number, end: number, size: number, safeStart: number, safeEnd: number, safeCenter: number): number {
  if (size > safeEnd - safeStart) return safeCenter - (start + end) / 2;
  if (start < safeStart) return safeStart - start;
  if (end > safeEnd) return safeEnd - end;
  return 0;
}

function finiteDimension(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function intersect(left: MapRect, right: MapRect): MapRect | null {
  const overlap = {
    left: Math.max(left.left, right.left),
    top: Math.max(left.top, right.top),
    right: Math.min(left.right, right.right),
    bottom: Math.min(left.bottom, right.bottom),
  };
  return overlap.left < overlap.right && overlap.top < overlap.bottom ? overlap : null;
}

function rectArea(rect: MapRect): number { return Math.max(0, rect.right - rect.left) * Math.max(0, rect.bottom - rect.top); }

function apertureFromRect(rect: MapRect): SafeMapAperture {
  const width = Math.max(0, rect.right - rect.left);
  const height = Math.max(0, rect.bottom - rect.top);
  return { ...rect, width, height, center: { x: rect.left + width / 2, y: rect.top + height / 2 } };
}
